import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as fabric from 'fabric';
import QRCode from 'qrcode';
import { Button } from '../ui/Button';
import { LayersPanel } from './designer/LayersPanel';
import { SignDesignDocumentV2 } from './designer/types';
import { useSignDesigner } from './designer/useSignDesigner';
import { ToolRail } from './designer/ToolRail';
import { ShapeKind } from './designer/shapeCatalog';
import { InspectorPanel } from './designer/InspectorPanel';
import { validateFabricQrForAdvance } from './designer/qrValidation';
import { ValidationPanel } from './designer/ValidationPanel';

interface Props {
  aspect: { w: number; h: number };       // print-area aspect ratio
  /** v2 canvas bounds — enables bleed/safe-zone overlays + drag clamping.
   *  When present the designer locks canvas to product.canvas_width_px × canvas_height_px
   *  and prevents user from dragging content outside the printable area. */
  canvasBounds?: {
    width_px: number;
    height_px: number;
    bleed_px: number;      // extra pixels around edge that WILL print but may be trimmed
    safe_zone_px: number;  // inset from edge where no critical content should sit
    aspect_ratio: number;
  };
  defaultQrTarget?: string;
  defaultPropertyName?: string;
  defaultPhone?: string;
  onExport: (pngBase64: string) => void;
  /** Optional v2 hook — fires with the raw compositor spec (server-render input) in
   *  addition to onExport (which fires with the client-side PNG raster). */
  onCompositorSpec?: (spec: any) => void;
  exportingLabel?: string;
  exportLoading?: boolean;
  designDocument?: SignDesignDocumentV2;
  onDraftAutosave?: (document: SignDesignDocumentV2) => void;
}

// Target 300 DPI on the long edge for print quality.
// Capped at 4000 to keep upload payload under API Gateway limit (~6 MB).
const EXPORT_TARGET_LONG_EDGE = (longEdgeInches: number) =>
  Math.min(4000, Math.max(1500, Math.round(longEdgeInches * 300)));

const SIGN_DESIGNER_FONTS_ID = 'intrlock-sign-designer-fonts';
const SIGN_DESIGNER_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&family=Open+Sans:wght@400;700&family=Lato:wght@400;700&family=Montserrat:wght@400;700;900&family=Playfair+Display:wght@400;700&family=Merriweather:wght@400;700&family=Bebas+Neue&family=Oswald:wght@400;700&family=Anton&family=Lobster&family=Pacifico&family=Dancing+Script:wght@400;700&family=Permanent+Marker&family=Caveat:wght@400;700&family=Shadows+Into+Light&family=Special+Elite&display=swap';

const FONTS: Array<{ label: string; family: string; sample?: string }> = [
  { label: 'Inter (sans)',          family: 'Inter' },
  { label: 'Helvetica',             family: 'Helvetica, Arial, sans-serif' },
  { label: 'Roboto',                family: 'Roboto, sans-serif' },
  { label: 'Open Sans',             family: '"Open Sans", sans-serif' },
  { label: 'Lato',                  family: 'Lato, sans-serif' },
  { label: 'Montserrat',            family: 'Montserrat, sans-serif' },
  { label: 'Oswald (cond.)',        family: 'Oswald, sans-serif' },
  { label: 'Bebas Neue',            family: '"Bebas Neue", sans-serif' },
  { label: 'Anton',                 family: 'Anton, sans-serif' },
  { label: 'Impact',                family: 'Impact, sans-serif' },
  { label: 'Playfair Display',      family: '"Playfair Display", serif' },
  { label: 'Merriweather',          family: 'Merriweather, serif' },
  { label: 'Georgia',               family: 'Georgia, serif' },
  { label: 'Times New Roman',       family: '"Times New Roman", Times, serif' },
  { label: 'Courier New',           family: '"Courier New", monospace' },
  { label: 'Special Elite (type.)', family: '"Special Elite", monospace' },
  { label: 'Lobster (script)',      family: 'Lobster, cursive' },
  { label: 'Pacifico (script)',     family: 'Pacifico, cursive' },
  { label: 'Dancing Script',        family: '"Dancing Script", cursive' },
  { label: 'Caveat (handwritten)',  family: 'Caveat, cursive' },
  { label: 'Shadows Into Light',    family: '"Shadows Into Light", cursive' },
  { label: 'Permanent Marker',      family: '"Permanent Marker", cursive' },
];

const SOLID_PRESET_COLORS = [
  '#000000', '#FFFFFF', '#FF3B30', '#FF9500', '#FFCC00',
  '#34C759', '#5AC8FA', '#007AFF', '#5856D6', '#AF52DE',
  '#FF2D55', '#A2845E',
];

const TEXT_PRESETS = [
  { label: 'Heading',    fontSize: 96, fontWeight: 'bold' as const },
  { label: 'Subheading', fontSize: 64, fontWeight: 'normal' as const },
  { label: 'Body',       fontSize: 40, fontWeight: 'normal' as const },
];

export const SignDesigner: React.FC<Props> = ({
  aspect, canvasBounds, defaultQrTarget, defaultPropertyName, defaultPhone, onExport, onCompositorSpec, exportLoading, exportingLabel = 'Generate mockup', designDocument, onDraftAutosave,
}) => {
  // Keep the 15 optional design families off every other Shadow page. The link
  // intentionally remains cached for the browser session after the designer is
  // first opened so returning to a draft does not flash fallback fonts.
  useEffect(() => {
    if (document.getElementById(SIGN_DESIGNER_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = SIGN_DESIGNER_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = SIGN_DESIGNER_FONTS_URL;
    document.head.appendChild(link);
  }, []);

  // If v2 bounds provided, they override the loose aspect prop. This locks the canvas
  // to the exact print dimensions of the selected product (non-negotiable per §28.518.99.49.d).
  const effectiveAspect = canvasBounds
    ? { w: canvasBounds.width_px, h: canvasBounds.height_px }
    : aspect;
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const fabRef = useRef<fabric.Canvas | null>(null);
  const [bgColor, setBgColor] = useState('#ffffff');
  const [selected, setSelected] = useState<fabric.Object | null>(null);
  const [, forceTick] = useState(0);
  const rerender = useCallback(() => forceTick(t => t + 1), []);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [validationCodes, setValidationCodes] = useState<string[]>([]);
  const skipNextSnapshotRef = useRef(false);

  // Canvas display size — fit inside ~560 keeping aspect ratio, leaving room for tools.
  const MAX = 560;
  const ratio = effectiveAspect.w / effectiveAspect.h;
  const displayW = ratio >= 1 ? MAX : Math.round(MAX * ratio);
  const displayH = ratio >= 1 ? Math.round(MAX / ratio) : MAX;
  // Display scale: display px per canvas px. Used to project bleed/safe-zone in canvas
  // units onto the on-screen render, and to clamp drag operations correctly.
  const displayScale = canvasBounds ? displayW / canvasBounds.width_px : 1;
  const bleedDisplay = canvasBounds ? canvasBounds.bleed_px * displayScale : 0;
  const safeZoneDisplay = canvasBounds ? canvasBounds.safe_zone_px * displayScale : 0;

  const snapshot = useCallback(() => {
    if (!fabRef.current) return;
    if (skipNextSnapshotRef.current) { skipNextSnapshotRef.current = false; return; }
    const json = JSON.stringify(fabRef.current.toJSON());
    setHistory(prev => {
      const trimmed = prev.slice(0, historyIdx + 1);
      const next = [...trimmed, json];
      return next.slice(-40);
    });
    setHistoryIdx(idx => Math.min(idx + 1, 39));
  }, [historyIdx]);

  // Synchronous mount BEFORE browser paint so Fabric can't measure a 0×0 element.
  // enableRetinaScaling:false avoids DPR-related blank-canvas issues seen on macOS.
  useLayoutEffect(() => {
    const el = canvasElRef.current;
    if (!el) return;
    el.width = displayW;
    el.height = displayH;
    const c = new fabric.Canvas(el, {
      width: displayW,
      height: displayH,
      backgroundColor: bgColor,
      preserveObjectStacking: true,
      selection: true,
      enableRetinaScaling: false,
    });
    c.setDimensions({ width: displayW, height: displayH });
    fabRef.current = c;
    c.on('object:added',    () => { snapshot(); c.requestRenderAll(); });
    c.on('object:modified', () => { snapshot(); c.requestRenderAll(); rerender(); });
    c.on('object:removed',  () => { snapshot(); c.requestRenderAll(); });

    // Drag/resize clamping to bleed bounds — non-negotiable per §28.518.99.49.d.
    // Only active when v2 canvasBounds are provided (i.e. product has typed constraints).
    if (canvasBounds) {
      const clampObj = (obj: fabric.Object) => {
        const minLeft = -bleedDisplay;
        const minTop = -bleedDisplay;
        const w = (obj.width || 0) * (obj.scaleX || 1);
        const h = (obj.height || 0) * (obj.scaleY || 1);
        const maxLeft = displayW + bleedDisplay - w;
        const maxTop  = displayH + bleedDisplay - h;
        obj.set({
          left: Math.max(minLeft, Math.min(obj.left || 0, maxLeft)),
          top:  Math.max(minTop,  Math.min(obj.top  || 0, maxTop)),
        });
        obj.setCoords();
      };
      c.on('object:moving', (e: any) => { if (e.target) clampObj(e.target); });
      c.on('object:scaling', (e: any) => { if (e.target) clampObj(e.target); });
    }
    c.on('selection:created', (e) => { setSelected(e.selected?.[0] || null); rerender(); });
    c.on('selection:updated', (e) => { setSelected(e.selected?.[0] || null); rerender(); });
    c.on('selection:cleared', () => { setSelected(null); rerender(); });
    setHistory([]); setHistoryIdx(-1);
    skipNextSnapshotRef.current = false;
    snapshot();
    c.renderAll();
    requestAnimationFrame(() => c.requestRenderAll());
    document.fonts?.ready?.then(() => c.requestRenderAll()).catch(() => {});
    return () => { c.dispose(); fabRef.current = null; };
    // eslint-disable-next-line
  }, [displayW, displayH]);

  useEffect(() => {
    const c = fabRef.current;
    if (!c) return;
    c.backgroundColor = bgColor;
    c.renderAll();
    snapshot();
    // eslint-disable-next-line
  }, [bgColor]);

  // ── Add tools ────────────────────────────────────────────────────────────

  const addText = (text?: string, opts: Partial<{ fontSize: number; fontWeight: 'normal' | 'bold'; fontFamily: string; fill: string }> = {}) => {
    const c = fabRef.current;
    if (!c) return;
    const t = new fabric.IText(text || 'Tap to edit', {
      left: c.getWidth() / 2 - 100,
      top: c.getHeight() / 2 - 30,
      fontSize: opts.fontSize ?? 48,
      fontWeight: opts.fontWeight ?? 'normal',
      fontFamily: opts.fontFamily ?? 'Inter',
      fill: opts.fill ?? '#111111',
    });
    c.add(t); c.setActiveObject(t); c.renderAll();
  };

  const addShape = (kind: 'rect' | 'circle' | 'line' | 'triangle' | 'star' | 'arrow') => {
    const c = fabRef.current;
    if (!c) return;
    const cx = c.getWidth() / 2;
    const cy = c.getHeight() / 2;
    const fill = '#007AFF';
    let obj: fabric.Object;
    if (kind === 'rect') {
      obj = new fabric.Rect({ left: cx - 60, top: cy - 40, width: 120, height: 80, fill });
    } else if (kind === 'circle') {
      obj = new fabric.Circle({ left: cx - 50, top: cy - 50, radius: 50, fill });
    } else if (kind === 'line') {
      obj = new fabric.Line([cx - 60, cy, cx + 60, cy], { stroke: fill, strokeWidth: 6 });
    } else if (kind === 'triangle') {
      obj = new fabric.Triangle({ left: cx - 50, top: cy - 50, width: 100, height: 100, fill });
    } else if (kind === 'star') {
      const r1 = 60, r2 = 26;
      const points: { x: number; y: number }[] = [];
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? r1 : r2;
        const a = (Math.PI / 5) * i - Math.PI / 2;
        points.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
      }
      obj = new fabric.Polygon(points, { fill });
    } else {
      // arrow: a line + triangle head, grouped
      const line = new fabric.Line([0, 20, 100, 20], { stroke: fill, strokeWidth: 8 });
      const head = new fabric.Triangle({ left: 96, top: 6, width: 28, height: 28, fill, angle: 90 });
      obj = new fabric.Group([line, head], { left: cx - 60, top: cy - 20 });
    }
    c.add(obj); c.setActiveObject(obj); c.renderAll();
  };

  const addQR = async (target?: string) => {
    const c = fabRef.current;
    if (!c) return;
    const url = target || defaultQrTarget || window.prompt('QR target URL?', 'https://intrlock.io') || '';
    if (!url) return;
    try {
      const dataUrl = await QRCode.toDataURL(url, { width: 512, margin: 1, errorCorrectionLevel: 'M' });
      const img = await fabric.FabricImage.fromURL(dataUrl);
      const w = c.getWidth(), h = c.getHeight();
      const targetSize = Math.min(w, h) * 0.4;
      const scale = targetSize / (img.width || 512);
      img.set({ left: w / 2 - targetSize / 2, top: h / 2 - targetSize / 2, scaleX: scale, scaleY: scale });
      (img as any).intrlockData = { type: 'qr', target: url };
      c.add(img); c.setActiveObject(img); c.renderAll();
      setValidationCodes([]);
    } catch (e) { console.error('QR add failed', e); }
  };

  const addImageFromFile = (file: File) => {
    const c = fabRef.current;
    if (!c) return;
    const r = new FileReader();
    r.onload = async () => {
      try {
        const img = await fabric.FabricImage.fromURL(String(r.result));
        const w = c.getWidth(), h = c.getHeight();
        const targetSize = Math.min(w, h) * 0.5;
        const naturalLong = Math.max(img.width || 1, img.height || 1);
        const scale = targetSize / naturalLong;
        img.set({
          left: w / 2 - (img.width || 0) * scale / 2,
          top: h / 2 - (img.height || 0) * scale / 2,
          scaleX: scale, scaleY: scale,
        });
        c.add(img); c.setActiveObject(img); c.renderAll();
      } catch (e) { console.error('image add failed', e); }
    };
    r.readAsDataURL(file);
  };

  // ── Selection editing ────────────────────────────────────────────────────

  const updateSelected = (patch: Record<string, unknown>) => {
    const c = fabRef.current;
    if (!c || !selected) return;
    selected.set(patch);
    selected.setCoords();
    c.requestRenderAll();
    rerender();
  };

  const setSelectedFontFamily = async (family: string) => {
    if (!selected || !(selected as any).isType?.('IText') && (selected as any).type !== 'i-text' && (selected as any).type !== 'text') return;
    try { await document.fonts.load(`16px ${family.split(',')[0].replace(/['"]/g, '')}`); } catch {}
    updateSelected({ fontFamily: family });
  };

  const deleteSelected = () => {
    const c = fabRef.current;
    if (!c || !selected) return;
    c.remove(selected);
    c.discardActiveObject();
    c.renderAll();
  };

  const duplicateSelected = async () => {
    const c = fabRef.current;
    if (!c || !selected) return;
    const clone = await selected.clone();
    clone.set({ left: (selected.left || 0) + 20, top: (selected.top || 0) + 20 });
    c.add(clone); c.setActiveObject(clone); c.renderAll();
  };

  const bringForward = () => {
    const c = fabRef.current;
    if (!c || !selected) return;
    c.bringObjectForward(selected, true);
    c.renderAll();
  };
  const sendBackward = () => {
    const c = fabRef.current;
    if (!c || !selected) return;
    c.sendObjectBackwards(selected, true);
    c.renderAll();
  };
  const bringToFront = () => {
    const c = fabRef.current;
    if (!c || !selected) return;
    c.bringObjectToFront(selected);
    c.renderAll();
  };
  const sendToBack = () => {
    const c = fabRef.current;
    if (!c || !selected) return;
    c.sendObjectToBack(selected);
    c.renderAll();
  };

  const alignCenterH = () => {
    const c = fabRef.current;
    if (!c || !selected) return;
    const objW = (selected.width || 0) * (selected.scaleX || 1);
    selected.set({ left: c.getWidth() / 2 - objW / 2 });
    selected.setCoords(); c.renderAll(); snapshot();
  };
  const alignCenterV = () => {
    const c = fabRef.current;
    if (!c || !selected) return;
    const objH = (selected.height || 0) * (selected.scaleY || 1);
    selected.set({ top: c.getHeight() / 2 - objH / 2 });
    selected.setCoords(); c.renderAll(); snapshot();
  };

  const flipHorizontal = () => updateSelected({ flipX: !(selected as any)?.flipX });
  const flipVertical   = () => updateSelected({ flipY: !(selected as any)?.flipY });

  // ── Undo / redo / clear ──────────────────────────────────────────────────

  const undo = () => {
    if (historyIdx <= 0) return;
    const c = fabRef.current;
    if (!c) return;
    skipNextSnapshotRef.current = true;
    c.loadFromJSON(history[historyIdx - 1]).then(() => c.renderAll());
    setHistoryIdx(historyIdx - 1);
  };
  const redo = () => {
    if (historyIdx >= history.length - 1) return;
    const c = fabRef.current;
    if (!c) return;
    skipNextSnapshotRef.current = true;
    c.loadFromJSON(history[historyIdx + 1]).then(() => c.renderAll());
    setHistoryIdx(historyIdx + 1);
  };
  const clearAll = () => {
    const c = fabRef.current;
    if (!c) return;
    if (!window.confirm('Clear the canvas?')) return;
    c.clear();
    c.backgroundColor = bgColor;
    c.renderAll();
  };

  // ── Export ───────────────────────────────────────────────────────────────

  const exportPng = () => {
    const c = fabRef.current;
    if (!c) return;
    const qrValidation = validateFabricQrForAdvance(
      c.getObjects() as any[],
      defaultQrTarget || '',
    );
    if (!qrValidation.valid) {
      setValidationCodes(qrValidation.codes);
      return;
    }
    setValidationCodes([]);
    c.discardActiveObject();
    c.renderAll();
    // Match the print-area aspect at 300 DPI, capped.
    const longInches = Math.max(aspect.w, aspect.h);
    const displayLong = Math.max(c.getWidth(), c.getHeight());
    const targetPx = canvasBounds
      ? Math.max(canvasBounds.width_px, canvasBounds.height_px) / displayLong
      : EXPORT_TARGET_LONG_EDGE(longInches) / displayLong;
    const multiplier = canvasBounds
      ? targetPx // exact 1:1 render at canvas pixel dims
      : EXPORT_TARGET_LONG_EDGE(longInches) / displayLong;
    const dataUrl = c.toDataURL({ format: 'png', multiplier, quality: 1 });
    if (onCompositorSpec && c) {
      // Serialize the fabric scene as a compositor spec for server-side rendering.
      const json: any = (c as any).toJSON(['intrlockData']);
      onCompositorSpec({
        version: 1,
        canvas: {
          width_px: canvasBounds?.width_px ?? Math.round(c.getWidth() / displayScale),
          height_px: canvasBounds?.height_px ?? Math.round(c.getHeight() / displayScale),
          background: bgColor,
        },
        objects: (json.objects || []).map((o: any, i: number) => ({
          ...o,
          id: `obj-${i}`,
          // Rescale from display coords → canvas coords when v2 bounds are active.
          left: canvasBounds ? (o.left || 0) / displayScale : o.left,
          top:  canvasBounds ? (o.top  || 0) / displayScale : o.top,
          width:  canvasBounds ? (o.width  || 0) / displayScale : o.width,
          height: canvasBounds ? (o.height || 0) / displayScale : o.height,
          zIndex: i,
        })),
      });
    }
    onExport(dataUrl);
  };

  // ── Derived selection state ──────────────────────────────────────────────

  const sel: any = selected;
  const isText = sel && (sel.type === 'i-text' || sel.type === 'text');
  const isShape = sel && ['rect', 'circle', 'triangle', 'polygon', 'line', 'group'].includes(sel.type);
  const isImage = sel && sel.type === 'image';

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-2">
      {designDocument && <DocumentLayersBridge document={designDocument} onAutosave={onDraftAutosave} />}
      <ToolRail
        onText={() => addText()}
        onQr={() => addQR()}
        onUpload={addImageFromFile}
        onShape={(kind: ShapeKind) => addShape(
          kind === 'circle' || kind === 'ellipse' ? 'circle'
          : kind === 'triangle' ? 'triangle'
          : kind === 'star' || kind === 'polygon' || kind === 'badge' ? 'star'
          : kind === 'line' || kind === 'divider' ? 'line'
          : kind === 'arrow' ? 'arrow' : 'rect'
        )}
      />
      {/* Add object bar */}
      <div className="flex flex-wrap gap-1">
        <Button size="sm" variant="secondary" onClick={() => addText()}>＋ Text</Button>
        {defaultPropertyName && (
          <Button size="sm" variant="secondary" onClick={() => addText(defaultPropertyName, { fontSize: 64, fontWeight: 'bold' })}>＋ Property name</Button>
        )}
        {defaultPhone && (
          <Button size="sm" variant="secondary" onClick={() => addText(defaultPhone, { fontSize: 48 })}>＋ Phone</Button>
        )}
        <Button size="sm" variant="secondary" onClick={() => addQR()}>＋ QR</Button>
        <label className="inline-flex items-center px-3 py-1.5 text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 rounded-lg cursor-pointer">
          ＋ Image
          <input type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) addImageFromFile(f); e.target.value = ''; }} />
        </label>
        <div className="border-l border-gray-300 mx-1"></div>
        <Button size="sm" variant="secondary" onClick={() => addShape('rect')}>▭</Button>
        <Button size="sm" variant="secondary" onClick={() => addShape('circle')}>●</Button>
        <Button size="sm" variant="secondary" onClick={() => addShape('triangle')}>▲</Button>
        <Button size="sm" variant="secondary" onClick={() => addShape('star')}>★</Button>
        <Button size="sm" variant="secondary" onClick={() => addShape('line')}>—</Button>
        <Button size="sm" variant="secondary" onClick={() => addShape('arrow')}>→</Button>
      </div>

      {/* Canvas-level controls */}
      <div className="flex flex-wrap gap-2 items-center text-xs">
        <label className="flex items-center gap-1">
          <span className="text-gray-600">Background</span>
          <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} className="w-7 h-7 border border-gray-300 rounded cursor-pointer" />
        </label>
        <div className="border-l border-gray-300 h-5"></div>
        <Button size="sm" variant="ghost" onClick={undo} disabled={historyIdx <= 0}>↶</Button>
        <Button size="sm" variant="ghost" onClick={redo} disabled={historyIdx >= history.length - 1}>↷</Button>
        <Button size="sm" variant="ghost" onClick={clearAll}>Clear</Button>
      </div>

      {/* Selection properties panel */}
      {selected && (
        <div className="bg-gray-50 border border-gray-200 rounded p-2 space-y-2 text-xs">
          <InspectorPanel value={sel} onChange={updateSelected} />
          {/* Common: position, rotation, opacity, layers */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-gray-500">{isText ? 'Text' : isShape ? 'Shape' : isImage ? 'Image' : 'Object'}</span>
            <Button size="sm" variant="ghost" onClick={duplicateSelected}>Duplicate</Button>
            <Button size="sm" variant="ghost" onClick={deleteSelected}>Delete</Button>
            <div className="border-l border-gray-300 h-5"></div>
            <Button size="sm" variant="ghost" onClick={alignCenterH}>↔ Center</Button>
            <Button size="sm" variant="ghost" onClick={alignCenterV}>↕ Center</Button>
            <Button size="sm" variant="ghost" onClick={flipHorizontal}>⇋</Button>
            <Button size="sm" variant="ghost" onClick={flipVertical}>⇵</Button>
            <div className="border-l border-gray-300 h-5"></div>
            <Button size="sm" variant="ghost" onClick={bringToFront} title="Bring to front">⤒</Button>
            <Button size="sm" variant="ghost" onClick={bringForward} title="Bring forward">↑</Button>
            <Button size="sm" variant="ghost" onClick={sendBackward} title="Send backward">↓</Button>
            <Button size="sm" variant="ghost" onClick={sendToBack} title="Send to back">⤓</Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-1">
              <span className="text-gray-600">Rotation</span>
              <input
                type="number"
                value={Math.round(sel.angle || 0)}
                onChange={e => updateSelected({ angle: Number(e.target.value) || 0 })}
                className="w-16 border border-gray-300 rounded px-1 py-0.5"
              />°
            </label>
            <label className="flex items-center gap-1">
              <span className="text-gray-600">Opacity</span>
              <input
                type="range" min={0} max={1} step={0.05}
                value={sel.opacity ?? 1}
                onChange={e => updateSelected({ opacity: Number(e.target.value) })}
                className="w-24"
              />
              <span className="text-gray-500 w-8 text-right">{Math.round((sel.opacity ?? 1) * 100)}%</span>
            </label>
          </div>

          {/* Text-specific */}
          {isText && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={sel.fontFamily}
                  onChange={e => setSelectedFontFamily(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-xs min-w-[180px]"
                  style={{ fontFamily: sel.fontFamily }}
                >
                  {FONTS.map(f => (
                    <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>
                      {f.label}
                    </option>
                  ))}
                </select>
                <input
                  type="number" min={8} max={400}
                  value={Math.round(sel.fontSize || 48)}
                  onChange={e => updateSelected({ fontSize: Number(e.target.value) || 48 })}
                  className="w-16 border border-gray-300 rounded px-1 py-0.5"
                />
                <Button size="sm" variant={sel.fontWeight === 'bold' ? 'primary' : 'ghost'}
                  onClick={() => updateSelected({ fontWeight: sel.fontWeight === 'bold' ? 'normal' : 'bold' })}>B</Button>
                <Button size="sm" variant={sel.fontStyle === 'italic' ? 'primary' : 'ghost'}
                  onClick={() => updateSelected({ fontStyle: sel.fontStyle === 'italic' ? 'normal' : 'italic' })}>I</Button>
                <Button size="sm" variant={sel.underline ? 'primary' : 'ghost'}
                  onClick={() => updateSelected({ underline: !sel.underline })}>U</Button>
                <div className="border-l border-gray-300 h-5"></div>
                {(['left', 'center', 'right'] as const).map(a => (
                  <Button key={a} size="sm" variant={sel.textAlign === a ? 'primary' : 'ghost'}
                    onClick={() => updateSelected({ textAlign: a })}>{a[0].toUpperCase()}</Button>
                ))}
              </div>
              <ColorRow label="Text" value={sel.fill || '#000000'} onChange={c => updateSelected({ fill: c })} />
              <ColorRow label="Outline" value={sel.stroke || '#000000'} onChange={c => updateSelected({ stroke: c, strokeWidth: c ? Math.max(sel.strokeWidth || 0, 2) : 0 })} optional />
            </>
          )}

          {/* Shape-specific */}
          {isShape && !isText && (
            <>
              <ColorRow label="Fill" value={sel.fill || '#000000'} onChange={c => updateSelected({ fill: c })} optional />
              <div className="flex flex-wrap items-center gap-3">
                <ColorRow label="Stroke" value={sel.stroke || '#000000'} onChange={c => updateSelected({ stroke: c })} optional inline />
                <label className="flex items-center gap-1">
                  <span className="text-gray-600">Stroke width</span>
                  <input type="number" min={0} max={50} value={sel.strokeWidth || 0}
                    onChange={e => updateSelected({ strokeWidth: Number(e.target.value) || 0 })}
                    className="w-14 border border-gray-300 rounded px-1 py-0.5" />
                </label>
              </div>
            </>
          )}
        </div>
      )}

      {/* Canvas */}
      <div
        className="flex items-center justify-center bg-gray-100 rounded-lg p-3"
        style={{ minHeight: displayH + 24 }}
      >
        {/* Wrapper carries the visual chrome (border/shadow) because Fabric v6
            clones any className onto the upper-canvas it generates, and an
            opaque bg on that overlay hides everything drawn on the lower one. */}
        <div
          className="border border-gray-300 shadow-sm"
          style={{ width: displayW, height: displayH, position: 'relative', background: bgColor }}
        >
          <canvas
            key={`${displayW}x${displayH}`}
            ref={canvasElRef}
            width={displayW}
            height={displayH}
            style={{ display: 'block', width: displayW, height: displayH }}
          />
          {canvasBounds && (
            <>
              {/* Bleed line (red dashed) — extends OUTSIDE the trim edge.
                  Anything past this line will NOT print. */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  left: -bleedDisplay,
                  top: -bleedDisplay,
                  width: displayW + bleedDisplay * 2,
                  height: displayH + bleedDisplay * 2,
                  border: '1px dashed rgba(255,59,48,0.9)',
                  pointerEvents: 'none',
                  boxSizing: 'border-box',
                }}
              />
              {/* Safe zone (yellow dashed) — keep critical content inside.
                  Content between trim edge and safe zone risks getting cut off. */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  left: safeZoneDisplay,
                  top: safeZoneDisplay,
                  width: displayW - safeZoneDisplay * 2,
                  height: displayH - safeZoneDisplay * 2,
                  border: '1px dashed rgba(255,204,0,0.9)',
                  pointerEvents: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </>
          )}
        </div>
      </div>

      {/* Quick-add text presets when nothing selected */}
      {!selected && (
        <div className="flex flex-wrap gap-1 text-xs">
          <span className="text-gray-500 self-center mr-1">Quick:</span>
          {TEXT_PRESETS.map(p => (
            <Button key={p.label} size="sm" variant="ghost" onClick={() => addText(p.label, p)}>{p.label}</Button>
          ))}
        </div>
      )}

      <div className="text-xs text-gray-500 text-center space-y-1">
        <div>
          Print size: {aspect.w}″ × {aspect.h}″ • Export resolution: {canvasBounds
            ? `${canvasBounds.width_px}×${canvasBounds.height_px}px (product-locked)`
            : `${EXPORT_TARGET_LONG_EDGE(Math.max(aspect.w, aspect.h))}px (≈300 DPI)`}
        </div>
        {canvasBounds && (
          <div className="flex items-center justify-center gap-3 text-[10px]">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 border-t border-dashed border-red-500"></span>
              Bleed (trimmed)
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 border-t border-dashed border-yellow-500"></span>
              Safe zone (keep content inside)
            </span>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={exportPng} loading={exportLoading} disabled={historyIdx < 0}>
          {exportingLabel}
        </Button>
      </div>
      <ValidationPanel
        codes={validationCodes}
        onAddQr={() => addQR(defaultQrTarget)}
        onFixQr={() => addQR(defaultQrTarget)}
      />
    </div>
  );
};

const DocumentLayersBridge: React.FC<{
  document: SignDesignDocumentV2;
  onAutosave?: (document: SignDesignDocumentV2) => void;
}> = ({ document, onAutosave }) => {
  const editor = useSignDesigner({ initialDocument: document, autosave: onAutosave });
  return (
    <LayersPanel
      layers={editor.document.layers}
      onSelect={() => {}}
      onRename={editor.actions.rename}
      onReorder={editor.actions.reorder}
      onToggleVisible={editor.actions.toggleVisible}
      onToggleLocked={editor.actions.toggleLocked}
    />
  );
};

const ColorRow: React.FC<{
  label: string;
  value: string;
  onChange: (c: string) => void;
  optional?: boolean;
  inline?: boolean;
}> = ({ label, value, onChange, optional, inline }) => (
  <div className={inline ? 'inline-flex items-center gap-1' : 'flex items-center gap-2 flex-wrap'}>
    <span className="text-gray-600 text-xs">{label}</span>
    <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-7 h-7 border border-gray-300 rounded cursor-pointer" />
    {!inline && (
      <div className="flex gap-1">
        {SOLID_PRESET_COLORS.map(c => (
          <button key={c} onClick={() => onChange(c)}
            style={{ backgroundColor: c }}
            className="w-5 h-5 rounded border border-gray-300 hover:scale-110 transition"
            aria-label={c} />
        ))}
        {optional && (
          <button onClick={() => onChange('')} className="w-5 h-5 rounded border border-gray-300 bg-white text-xs text-gray-500 hover:scale-110 transition" title="None">∅</button>
        )}
      </div>
    )}
  </div>
);

// Helper to parse "7\" x 5\"" or "2\" × 2\"" from a Printify variant title.
export function parseVariantAspect(title: string): { w: number; h: number } | null {
  const m = title.match(/(\d+(?:\.\d+)?)\s*[″"']?\s*[x×]\s*(\d+(?:\.\d+)?)/i);
  if (!m) return null;
  const w = parseFloat(m[1]);
  const h = parseFloat(m[2]);
  if (!w || !h) return null;
  return { w, h };
}
